import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import CmsGallery from '../components/CmsGallery';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/cms';

export default function Aflastning() {
  const whoIsItFor = [
    'Familier med et barn, en ung eller en voksen med komplekse udfordringer',
    'Bosteder og institutioner, der har brug for ekstra ressourcer i perioder',
    'Situationer, hvor der er behov for en pause i hverdagen – uden at gå på kompromis med borgerens tryghed',
  ];

  return (
    <>
      <SEO
        title="Aflastning – hjemme og ude - Akut Vikaren ApS"
        description="Fleksibel aflastning i hele Jylland og på Fyn, der giver familier og bosteder et nødvendigt afbræk – både i hjemmet og på aflastningsophold uden for hjemmet."
        ogImage="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/image00005.webp"
      />

      {/* Hero Header */}
      <section className="relative bg-[#041c2c] text-white py-16 sm:py-24 border-b border-slate-800 overflow-hidden">
        {/* Background image with dark transparent overlay */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/image00005.webp"
            alt="Aflastning – to voksne og to børn på bænk i park"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 35%' }}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#041c2c]/90 via-[#041c2c]/75 to-[#041c2c]/55" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/80 border border-slate-700/80 text-xs font-semibold text-[#b39366] uppercase tracking-wider">
              <i className="fa-solid fa-hand-holding-heart text-xs" aria-hidden="true"></i>
              <span>Ydelse</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Aflastning – hjemme og ude
            </h1>
            <p className="text-slate-300 text-lg sm:text-xl font-normal leading-relaxed">
              Tryghed for borgeren – og et nødvendigt afbræk for familien
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Main Column */}
            <div className="lg:col-span-8 space-y-12">
              {/* Intro section */}
              <div className="space-y-4 text-slate-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Akutvikaren tilbyder fleksibel aflastning til familier og bosteder i hele Jylland og på Fyn. Vi ved, at det er krævende at have ansvaret for en pårørende med komplekse udfordringer i hverdagen – og at både familie og borger nogle gange har brug for aflastning.
                </p>
                <p>
                  Vores aflastning tilrettelægges individuelt og kan foregå både i hjemmet og uden for hjemmet, alt efter hvad der giver mest mening for den enkelte familie eller institution.
                </p>
              </div>

              {/* 2-column feature: Afløsning i hjemmet & Aflastning uden for hjemmet */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-[#faf9f6] border border-[#eae6dd] rounded-xl p-6 sm:p-7 space-y-3">
                  <div className="w-10 h-10 rounded-lg bg-[#b39366]/20 flex items-center justify-center text-[#b39366]">
                    <i className="fa-solid fa-house text-base" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#041c2c]">Afløsning i hjemmet</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Vi tilbyder afløsning i hjemmet, hvor en af vores erfarne medarbejdere kommer og aflaster forældre eller andre pårørende. Det kan være få timer om ugen eller mere faste og hyppige forløb. Formålet er at give familien overskud, samtidig med at borgeren oplever tryghed og genkendelighed.
                  </p>
                </div>

                <div className="bg-[#faf9f6] border border-[#eae6dd] rounded-xl p-6 sm:p-7 space-y-3">
                  <div className="w-10 h-10 rounded-lg bg-[#b39366]/20 flex items-center justify-center text-[#b39366]">
                    <i className="fa-solid fa-tree text-base" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#041c2c]">Aflastning uden for hjemmet</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Når der er behov for et længerevarende aflastningsforløb, tilbyder vi også aflastningsophold uden for hjemmet. Her kommer borgeren i trygge rammer med erfarne medarbejdere, der er vant til at håndtere borgere med komplekse udfordringer, såsom ADHD, autisme, personlighedsforstyrrelser, misbrug, omsorgssvigt, udadreagerende og selvskadende adfærd.
                  </p>
                </div>
              </div>

              {/* Sektion 3: Hvem er aflastning til? */}
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
                  Hvem er aflastning til?
                </h2>
                <p className="text-slate-700 text-base leading-relaxed">
                  Aflastning er relevant for:
                </p>

                <div className="bg-[#faf9f6] border border-[#eae6dd] rounded-xl p-6 sm:p-8">
                  <ul className="space-y-3.5">
                    {whoIsItFor.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-800 text-base">
                        <span className="w-5 h-5 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center text-xs shrink-0 mt-0.5 font-bold">
                          <i className="fa-solid fa-check text-[10px]" aria-hidden="true"></i>
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Sektion 4: Fleksibilitet efter behov */}
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
                  Fleksibilitet efter behov
                </h2>
                <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
                  Aflastning kan aftales som enkeltstående timer, faste ugentlige forløb eller længere ophold. Vi tilpasser altid indsatsen til den konkrete situation og samarbejder med kommune, familie og øvrige samarbejdspartnere om det rette omfang.
                </p>
              </div>

              {/* Sektion 5: Dækningsområde */}
              <div className="bg-[#f8f7f4] border-l-4 border-[#b39366] rounded-r-xl p-6 space-y-2">
                <h2 className="text-xl font-bold text-[#041c2c]">
                  Dækningsområde
                </h2>
                <p className="text-slate-700 text-base leading-relaxed">
                  Vi leverer aflastning i hele Jylland og på Fyn. (Bemærk: ved akut anbringelse modtager vi borgere fra hele landet – <Link to="/akut-anbringelse" className="text-[#b39366] font-semibold underline hover:text-[#a18155]">læs mere under den ydelse</Link>.)
                </p>
              </div>

              {/* CMS Galleri for Aflastning */}
              <div className="pt-6 border-t border-slate-200">
                <CmsGallery
                  gallerySlug="aflastning"
                  sectionTitle="Billeder fra aflastning og aktiviteter"
                  sectionSubtitle="Oplev hverdagsglimt af trygge rammer og støttende relationer."
                />
              </div>
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-4 space-y-8">
              {/* CTA callout */}
              <div className="bg-[#041c2c] text-white rounded-2xl p-6 sm:p-8 space-y-4 sticky top-24">
                <h3 className="text-xl font-bold text-white">
                  Vil du høre mere om vores aflastningstilbud?
                </h3>
                <p className="text-slate-300 text-sm sm:text-base">
                  Kontakt os for en uforpligtende snak om jeres behov.
                </p>
                <div className="flex flex-col gap-3 pt-2">
                  <a
                    href="tel:+4523964042"
                    className="inline-flex items-center justify-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white px-4 py-2.5 rounded font-semibold text-sm transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                    <span>Ring (+45) 23 96 40 42</span>
                  </a>
                  <a
                    href="mailto:kontakt@akutvikaren.dk"
                    className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2.5 rounded font-medium text-sm transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope text-xs" aria-hidden="true"></i>
                    <span>kontakt@akutvikaren.dk</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
